import React, { useEffect, useState } from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { useForm } from "react-hook-form";
import { ReactComponent as RadioButtonIcon } from "../../../assets/Icons/radio-button.svg";
import { ReactComponent as RadioButtonMarkedIcon } from "../../../assets/Icons/radio-button-marked.svg";
import { useOnboarding } from "../../../contexts/onboarding";
import {
  independent_mental_health_professional,
  group_practice,
  referring_physician,
  treatment_program,
} from "../../../constants/common";
import "./style.css";
import CustomButton from "../../CustomButton";
import { trackClickEvent } from "../../../mixpanel";

const accountTypes = {
  [independent_mental_health_professional]:
    "Independent Mental Health Professional",
  [group_practice]: "Group Practice Owner or Representative",
  [referring_physician]: "Referring Physician",
  [treatment_program]: "Treatment Program Owner or Representative",
};

const BusinessDescription = ({ payment, TP }) => {
  const { next, onboardingState, updateOnboardingState, setOnboardingState } =
    useOnboarding();
  const { handleSubmit, setValue, watch } = useForm({
    defaultValues: {
      account_type:
        onboardingState.account_type || independent_mental_health_professional,
    },
  });

  const accountType = watch("account_type");
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [showError, setShowError] = useState(false);
  const [pendingAccountType, setPendingAccountType] = useState(null);
  const [authConfirmed, setAuthConfirmed] = useState(false);
  const [previousAccountType, setPreviousAccountType] = useState(null);

  // Set independent_mental_health_professional as default on page load if no account_type is set
  useEffect(() => {
    console.log("mount", onboardingState.account_type, accountType);
    if (!onboardingState.account_type && !accountType) {
      setValue("account_type", independent_mental_health_professional);
      updateOnboardingState({
        account_type: independent_mental_health_professional,
      });
    }
  }, []); // Run only on mount

  useEffect(() => {
    // When dialog opens, check if this is an already saved selection
    if (showAuthDialog && pendingAccountType) {
      // Pre-check if the user clicked on the same type they already had selected
      // This means previousAccountType === pendingAccountType (they were the same before we changed it)
      // OR if it matches the saved onboardingState.account_type and previousAccountType was empty/undefined
      const isAlreadySaved =
        previousAccountType === pendingAccountType ||
        (pendingAccountType === onboardingState.account_type &&
          !previousAccountType);
      setAuthConfirmed(isAlreadySaved);
    }
  }, [
    showAuthDialog,
    pendingAccountType,
    onboardingState.account_type,
    previousAccountType,
  ]);

  const clearUserData = () => {
    setOnboardingState((prevState) => ({
      email: prevState.email,
      password: prevState.password,
      first_name: prevState.first_name,
      last_name: prevState.last_name,
      account_type: prevState.account_type,
    }));
  };

  const handleAccountTypeChange = (type) => {
    // Show auth dialog for group practice or treatment program
    if (type === group_practice || type === treatment_program) {
      // Store the current account type before changing (to restore if cancelled)
      const currentType = accountType || onboardingState.account_type || "";
      setPreviousAccountType(currentType);

      // Immediately set the new type so it shows as checked
      setValue("account_type", type);
      updateOnboardingState({ account_type: type });

      // Set up dialog state
      setPendingAccountType(type);
      setAuthConfirmed(false); // Reset auth confirmation when selecting a new type
      setShowAuthDialog(true);
      setShowError(false);
    } else {
      // For other account types, directly apply the selection
      setValue("account_type", type);
      updateOnboardingState({ account_type: type });
      if (onboardingState.account_type !== type) clearUserData();
      setPendingAccountType(null);
      setPreviousAccountType(null);
    }
  };

  const handleDialogClose = () => {
    // Restore previous account type when dialog is closed without confirming
    if (pendingAccountType && !authConfirmed) {
      // Restore the previous account type
      const typeToRestore = previousAccountType || "";
      setValue("account_type", typeToRestore);
      updateOnboardingState({ account_type: typeToRestore });
      // Don't clear user data when restoring - we're reverting the change
    }
    setPendingAccountType(null);
    setPreviousAccountType(null);
    setShowAuthDialog(false);
    setAuthConfirmed(false);
  };

  const handleDialogContinue = () => {
    if (authConfirmed && pendingAccountType) {
      // Apply the pending selection
      setValue("account_type", pendingAccountType);
      updateOnboardingState({ account_type: pendingAccountType });
      if (onboardingState.account_type !== pendingAccountType) clearUserData();

      // Close dialog
      setShowAuthDialog(false);
      setAuthConfirmed(false);
      setPendingAccountType(null);
      setPreviousAccountType(null);
    }
  };

  const onSubmit = () => {
    if (accountType) {
      // Check if auth confirmation is required
      if (accountType === group_practice || accountType === treatment_program) {
        // Show dialog if not already authorized
        if (accountType !== onboardingState.account_type) {
          setPendingAccountType(accountType);
          setAuthConfirmed(false); // Ensure checkbox is unchecked for new selection
          setShowAuthDialog(true);
          setShowError(false);
          return;
        }
      }
      updateOnboardingState({
        account_type: accountType,
      });
      
      trackClickEvent(`Onboard as ${accountTypes[accountType]}`, {
        page: "Register",
        component: "account type form",
        button: "Continue",
      });
      next();
    }
  };

  const getAuthMessage = () => {
    if (pendingAccountType === group_practice) {
      return "By selecting this option, you confirm that you're authorized to register this account on behalf of the group practice. If you're not authorized, please contact your administrator.";
    }
    return "By selecting this option, you confirm that you're authorized to register this account on behalf of the treatment program. If you're not authorized, please contact your administrator.";
  };

  const getCheckboxLabel = () => {
    if (pendingAccountType === group_practice) {
      return "I’m authorized to register on behalf of this group practice.";
    }
    return "I’m authorized to register on behalf of this treatment program.";
  };

  return (
    <Container maxWidth="md">
      <div className="cardShadow">
        <Typography variant="h5" mb={3}>
          Which of the following describes your business?
        </Typography>
        {Object.entries(accountTypes).map(([type, label], index) => (
          <Button
            key={type}
            variant="outlined"
            color="secondary"
            fullWidth
            sx={{
              color: "text.primary",
              mb: 3,
              gap: "6px",
              justifyContent: "flex-start",
              minHeight: 65,
              fontSize: 16,
              fontWeight: 400,
            }}
            disabled={
              (TP === "1" && type !== treatment_program) ||
              (payment === "Special_Offer" && type === treatment_program)
            }
            startIcon={
              accountType === type ? (
                <RadioButtonMarkedIcon />
              ) : (
                <RadioButtonIcon />
              )
            }
            onClick={() => handleAccountTypeChange(type)}
          >
            {label}
          </Button>
        ))}
      </div>

      <Box pt={5} textAlign="center">
        {showError && (
          <Alert severity="error" sx={{ mb: 2, maxWidth: 400, mx: "auto" }}>
            Please confirm you're authorized to register before continuing.
          </Alert>
        )}
        <CustomButton
          className="btnContinue"
          variant="contained"
          sx={{
            minWidth: 212,
          }}
          onClick={handleSubmit(onSubmit)}
          disabled={!accountType}
          name="Continue"
          page="Register"
          eventName="Step - 2 Business Description"
        >
          Continue
        </CustomButton>
      </Box>

      <Dialog
        open={showAuthDialog}
        onClose={handleDialogClose}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: "400px",
            maxWidth: "400px",
          },
        }}
      >
        <Box sx={{ position: "relative" }}>
          <IconButton
            aria-label="close"
            onClick={handleDialogClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[600],
              zIndex: 1,
            }}
          >
            <CloseIcon />
          </IconButton>

          <DialogContent sx={{ p: 4, pt: 6 }}>
            <Typography variant="h6" mb={2}>
              Authorization Confirmation
            </Typography>
            <Typography mb={3}>{getAuthMessage()}</Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={authConfirmed}
                  onChange={(e) => setAuthConfirmed(e.target.checked)}
                />
              }
              label={getCheckboxLabel()}
            />
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 0 }}>
            <Button
              variant="contained"
              onClick={handleDialogContinue}
              disabled={!authConfirmed}
            >
              Continue
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Container>
  );
};

export default BusinessDescription;
